import { useEffect, useMemo, useState } from "react"
import { dateToString, Labels } from "../../../../../features/data-types"
import { Column, DateField, FieldType, FieldValue, O, option, SelectorTable, TextField } from "../../../../../features/ui"
import { LotAttribute, LotAttributes } from "../../../../products"
import { toDate } from "../../../../../utils/typeUtils"

type Props = {
    value?: LotAttributes
    onChange: (lotAttributes: LotAttributes) => void
}

export const LotAttributeTable = (props: Props) => {
    const { value, onChange } = props

    const [lotAttributes, setLotAttributes] = useState<LotAttributes>(value || {})

    useEffect(() => {
        setLotAttributes(value || {})
    }, [value])

    const rowData = (lotAttr: string) => ({ lotAttr, lotAttrValue: lotAttr })
    const rowDataAsync = async (lotAttr: string) => rowData(lotAttr)
    const rows = useMemo(() => Object.keys(lotAttributes).map(rowData), [lotAttributes])

    const onChangeValue = (lotAttr: LotAttribute) => (value?: FieldValue) => {
        const valueStr = value as O<string>
        const newValue = lotAttr === LotAttribute.DUE_DATE ? dateToString(valueStr) : valueStr
        const newLotAttributes = { ...lotAttributes, [lotAttr]: newValue }
        setLotAttributes(newLotAttributes)
        onChange(newLotAttributes)
    }

    const columns: Column[] = [
        {
            name: 'lotAttr',
            type: FieldType.TEXT,
            label: 'Atributo',
            render: (value?: FieldValue) => Labels.lotAttributeLabel(value as LotAttribute)
        },
        {
            name: 'lotAttrValue',
            type: FieldType.TEXT,
            label: 'Valor',
            render: (value?: FieldValue) => {
                const lotAttr = value! as LotAttribute
                const lotAttrValue = lotAttributes[lotAttr]
                return lotAttr === LotAttribute.DUE_DATE ? (
                    <DateField
                        id={lotAttr}
                        value={toDate(lotAttrValue)}
                        onChange={onChangeValue(lotAttr)}
                    />
                ) : (
                    <TextField
                        id={lotAttr}
                        value={lotAttrValue}
                        freeValue
                        onChange={onChangeValue(lotAttr)}
                    />
                )
            }
        }
    ]

    const options = useMemo(() =>
        Object.values(LotAttribute).map(attr => option(attr, Labels.lotAttributeLabel(attr)))
    , [])

    return (
        <SelectorTable
            rowId="lotAttr"
            rowsData={rows}
            rowData={rowDataAsync}
            columns={columns}
            options={options}
        />
    )
}
